import {ModalType} from "data/enums";
import type {IModalsStore} from "data/stores/modals/modals.store";
import type {IUserStore} from "data/stores/user/user.store";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {first} from "lodash";
import {action, makeAutoObservable, observable} from "mobx";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {GAMEZONE_LINKS_MAPPER, OTHER_LANG_URL, OVERRIDE_LANG, TEAM_DATA} from "data/constants";
import {Bindings} from "data/constants/bindings";
import {useLocation} from "react-router-dom";

interface IMenuItem {
	url: string;
	label: string;
	children?: {url: string; label: string}[];
}

interface IParams {
	location: ReturnType<typeof useLocation>;
}

export interface IHeaderController extends ViewController<IParams> {
	get isAuthorized(): boolean;

	get rootPath(): string;

	get i18n(): ILocalizationStore;

	get isOpenDrawer(): boolean;

	get userInitials(): string;

	get userFullName(): string;

	get mainMenuList(): IMenuItem[];

	get staticPagesMenuList(): IMenuItem[];

	get localeUrls(): IMenuItem[];

	get guestMenuList(): {
		clickEvent: () => void;
		label: string;
	}[];

	get gameZoneLink(): string;

	toggleDrawer: (value: boolean) => void;

	openLoginModal(): void;

	openRegistrationModal(): void;
}

@injectable()
export class HeaderController implements IHeaderController {
	@observable public _location: IParams["location"] | null = null;
	@observable public _drawerState: boolean = false;

	constructor(
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore,
		@inject(Bindings.LocalizationStore) public i18n: ILocalizationStore
	) {
		makeAutoObservable(this);
	}

	get isAuthorized(): boolean {
		return this._userStore.isAuthorized;
	}

	get rootPath(): string {
		return "/dashboard";
	}

	get userInitials(): string {
		const firstName = first(this._userStore.user?.firstName) || "";
		const lastName = first(this._userStore.user?.lastName) || "";

		return `${firstName}${lastName}`.toUpperCase();
	}

	get userFullName(): string {
		const firstName = this._userStore.user?.firstName || "";
		const lastName = this._userStore.user?.lastName || "";

		return `${firstName} ${lastName}`;
	}

	get isOpenDrawer(): boolean {
		return this._drawerState;
	}

	get mainMenuList() {
		return [
			{
				url: this.rootPath,
				label: this.i18n.t("nav.main.my_picks", "My Picks"),
			},
			{
				url: "standings",
				label: this.i18n.t("nav.main.standings", "Leaderboard"),
			},
			{
				url: "leagues",
				label: this.i18n.t("nav.main.leagues", "Leagues"),
				children: [
					{
						url: "leagues/create",
						label: this.i18n.t("nav.main.leagues_create", "Create A League"),
					},
					{
						url: "leagues/join",
						label: this.i18n.t("nav.main.leagues_join", "Join A League"),
					},
				],
			},
		];
	}

	get guestMenuList() {
		return [
			{
				clickEvent: () => this.openLoginModal(),
				label: this.i18n.t("nav.main.login", "Login"),
			},
			{
				clickEvent: () => this.openRegistrationModal(),
				label: this.i18n.t("nav.main.registration", "Registration"),
			},
		];
	}

	get staticPagesMenuList() {
		const links = [
			{
				url: "/help",
				label: this.i18n.t("nav.main.help", "Help"),
			},
		];

		if (!TEAM_DATA.hidePrizePage) {
			links.unshift({
				url: "/prizes",
				label: this.i18n.t("nav.main.prizes", "Prizes"),
			});
		}

		try {
			const isUrlCorrect = new URL(OTHER_LANG_URL);
			if (isUrlCorrect) {
				const lang = OVERRIDE_LANG === "en" ? "fr" : "en";
				links.push({
					url: `${OTHER_LANG_URL}?lang=${lang}`,
					label: this.i18n.t(`nav.main.${lang}_lang_site`, `${lang}`),
				});
			}
		} catch (e) {
			// ignore errors for link
		}

		return links;
	}

	get localeUrls() {
		return [
			{
				url: "https://gamezone.cfl.ca/blitzpicks/",
				label: this.i18n.t("nav.main.blitzpicks", "Blitz Picks"),
			},
			{
				url: "https://gamezone.cfl.ca",
				label: this.i18n.t("nav.main.gamezone", "GameZone"),
			},
			{
				url: "https://gamezone.cfl.ca/pickem/",
				label: this.i18n.t("nav.main.pickem", "Pick'em"),
			},
			{
				url: "https://gamezone.cfl.ca/teampicker/",
				label: this.i18n.t("nav.main.teampicker", "Team Picker"),
			},
			{
				url: "https://gamezone.cfl.ca/fantasy/",
				label: this.i18n.t("nav.main.fantasy", "Fantasy"),
			},
			{
				url: "https://gamezone.cfl.ca/futures/",
				label: this.i18n.t("nav.main.futures", "Futures"),
			},
			{
				url: "https://gamezone.cfl.ca/allstar/",
				label: this.i18n.t("nav.main.all_star", "All-Star"),
			},
		];
	}

	@action
	toggleDrawer = (value: boolean): void => {
		this._drawerState = value;
	};

	openLoginModal(): void {
		this._modalsStore.showModal(ModalType.LOGIN);
	}

	openRegistrationModal(): void {
		this._modalsStore.showModal(ModalType.REGISTRATION);
	}

	init(param: IParams): void | Promise<void> {
		this._location = param.location;
	}

	onChange(param: IParams) {
		void this.init(param);
	}

	public get gameZoneLink(): string {
		const locale = (this.i18n.locale ?? "en-US") as keyof typeof GAMEZONE_LINKS_MAPPER;
		return GAMEZONE_LINKS_MAPPER[locale];
	}
}
